import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RefreshIcon from "@mui/icons-material/Refresh";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PublicIcon from "@mui/icons-material/Public";
import MapIcon from "@mui/icons-material/Map";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { PermitType, ZoneCoordinate } from "common/types/permits";
import { formatCurrency } from "common/format/currency";
import { formatRenewalPeriod, formatVehicleScope, formatUsageFeeApplicability } from "common/format/permits";

export interface PermitTypeWithZones extends PermitType {
  zones: ZoneCoordinate[];
}

export interface PermitTypeCardContentProps {
  permitType: PermitTypeWithZones;
  isSelected: boolean;
  isDocsExpanded: boolean;
  onCardClick: () => void;
  onDocsToggle: () => void;
  onViewZonesClick: () => void;
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Box sx={{ color: "primary.main", mt: 0.25 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export const PermitTypeCardContent: React.FC<PermitTypeCardContentProps> = ({
  permitType,
  isSelected,
  isDocsExpanded,
  onCardClick,
  onDocsToggle,
  onViewZonesClick,
}) => {
  const {
    name,
    description,
    pricePerRenewalPeriod,
    renewalPeriod,
    vehicleScope,
    zoneCoverageType,
    requiredDocumentTypes,
    zones,
    usageFeeApplicability,
  } = permitType;

  const usageFeeNotice = formatUsageFeeApplicability(usageFeeApplicability);

  const isJurisdictionWide = zoneCoverageType === "jurisdiction_wide";
  const documentCount = requiredDocumentTypes?.length ?? 0;

  const handleDocsToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDocsToggle();
  };

  const handleViewZones = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewZonesClick();
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCardClick();
  };

  return (
    <Card
      onClick={onCardClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        border: 2,
        borderColor: isSelected ? "primary.main" : "divider",
        boxShadow: isSelected ? 4 : 1,
        "&:hover": {
          boxShadow: 3,
          borderColor: isSelected ? "primary.main" : "primary.light",
        },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Header with name and selected chip */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {name}
          </Typography>
          {isSelected && <Chip label="Selected" color="primary" size="small" sx={{ flexShrink: 0 }} />}
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {/* Info Grid - 2x2 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <InfoItem
            icon={<AttachMoneyIcon fontSize="small" />}
            label="Permit Fee"
            value={pricePerRenewalPeriod === 0 ? "Free" : `$${formatCurrency(pricePerRenewalPeriod)}`}
          />
          <InfoItem
            icon={<RefreshIcon fontSize="small" />}
            label="Renewal"
            value={formatRenewalPeriod(renewalPeriod)}
          />
          <InfoItem
            icon={<DirectionsCarIcon fontSize="small" />}
            label="Applicability"
            value={formatVehicleScope(vehicleScope)}
          />

          {/* Coverage - either jurisdiction wide badge or view zones button */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ color: "primary.main", mt: 0.25 }}>
              {isJurisdictionWide ? <PublicIcon fontSize="small" /> : <MapIcon fontSize="small" />}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" component="p">
                Coverage
              </Typography>
              {isJurisdictionWide ? (
                <Chip
                  label="Jurisdiction Wide"
                  size="small"
                  variant="outlined"
                  icon={<PublicIcon sx={{ fontSize: 14 }} />}
                />
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                  onClick={handleViewZones}
                >
                  View {zones.length} Zone{zones.length !== 1 ? "s" : ""}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Usage Fee Notice */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {usageFeeNotice}
        </Typography>

        {/* Required Documents - Collapsible */}
        {documentCount > 0 && (
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
            <Button
              fullWidth
              onClick={handleDocsToggle}
              startIcon={<DescriptionIcon fontSize="small" />}
              endIcon={isDocsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                justifyContent: "space-between",
                textTransform: "none",
                color: "text.primary",
                fontWeight: 500,
                px: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Required Documents ({documentCount})
            </Button>
            <Collapse in={isDocsExpanded}>
              <List dense disablePadding sx={{ pl: 1 }}>
                {requiredDocumentTypes?.map((docType) => (
                  <ListItem key={docType.id} disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`• ${docType.name}`}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        )}
      </CardContent>

      {/* Select Button */}
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button fullWidth variant={isSelected ? "contained" : "outlined"} onClick={handleSelect}>
          {isSelected ? "Selected" : "Select This Permit"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default PermitTypeCardContent;
